/**
 * - Handles query parameter inputs for the API request.
 * - Allows adding, editing, enabling/disabling, and deleting params
 * - Generates a live URL preview based on enabled parameters
 */

import { Box, TextField, Typography, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Checkbox } from '@mui/material';

interface QueryParam {
  name: string;
  value: string;
  enabled: boolean;
}

interface ParamTabProps {
  params: QueryParam[];
  setParams: React.Dispatch<React.SetStateAction<QueryParam[]>>;
  url: string;
  viewMode: boolean;
}

const ParamTab = ({ params, setParams, url, viewMode }: ParamTabProps) => {
  // Generate URL preview based on enabled query parameters
  const getUrlPreview = () => {
    const enabledParams = params.filter((p) => p.enabled && p.name.trim());

    if (!url && enabledParams.length === 0) {
      return '';
    }

    let baseUrl = url || 'http://';
    const queryString = enabledParams
      .map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.value)}`)
      .join('&');

    if (!queryString) {
      return baseUrl;
    }

    // Remove trailing slash from baseUrl if present (to avoid issues when appending query params)
    if (baseUrl.endsWith('/') && !baseUrl.includes('?')) {
      baseUrl = baseUrl.slice(0, -1);
    }

    // Check if baseUrl already contains a '?' to decide on connector
    const connector = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${connector}${queryString}`;
  };

  const urlPreview = getUrlPreview();

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        fullWidth
        size="small"
        id="outlined-read-only-input"
        label="URL PREVIEW"
        InputLabelProps={{ shrink: true }}
        value={urlPreview}
        placeholder="https://api.open-meteo.com/v1/forecast"
        slotProps={{
          input: {
            readOnly: true,
            sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
          },
        }}
      />
      <Typography sx={{ mt: 2, mb: 2, fontWeight: 600, textAlign: 'left' }}>
        QUERY PARAMETERS
      </Typography>

      {params.map((param, index) => {
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              width: '100%',
            }}
          >
            <TextField
              id="outlined-basic"
              label="name"
              size="small"
              variant="outlined"
              sx={{ flex: 3, mr: 2 }}
              value={param.name}
              disabled={viewMode || !param.enabled}
              onChange={(event) => {
                const newName = event.target.value;
                setParams((prev) => {
                  let updatedParams = [...prev];
                  updatedParams[index] = { ...updatedParams[index], name: newName };
                  return updatedParams;
                });
              }}
            />
            <TextField
              id="outlined-basic"
              label="value"
              size="small"
              variant="outlined"
              sx={{ flex: 3, mr: 2 }}
              value={param.value}
              disabled={viewMode || !param.enabled}
              onChange={(event) => {
                const newValue = event.target.value;
                setParams((prev) => {
                  let updatedParams = [...prev];
                  updatedParams[index] = { ...updatedParams[index], value: newValue };
                  return updatedParams;
                });
              }}
            />
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: 70 }}
            >
              <Checkbox
                checked={param.enabled}
                disabled={viewMode}
                onChange={(event) => {
                  const isChecked = event.target.checked;
                  setParams((prev) => {
                    let updatedParams = [...prev];
                    updatedParams[index] = { ...updatedParams[index], enabled: isChecked };
                    return updatedParams;
                  });
                }}
              />
              <DeleteIcon
                sx={{
                  cursor: viewMode ? 'default' : 'pointer',
                  ml: 1,
                  color: viewMode ? 'default' : 'pointer',
                  '&:hover': {
                    color: viewMode ? 'action.disabled' : 'error.main',
                  },
                }}
                onClick={() => {
                  if (viewMode) return;
                  if (params.length === 1) {
                    setParams([{ name: '', value: '', enabled: false }]);
                  } else {
                    setParams(params.filter((_, i) => i !== index));
                  }
                }}
              />
            </Box>
          </Box>
        );
      })}

      {!viewMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() =>
              setParams((prev) => [
                ...prev,
                {
                  name: '',
                  value: '',
                  enabled: false,
                },
              ])
            }
          >
            + Add
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ParamTab;
